import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Leaf, Save, RefreshCw, Power } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const PandaParadiseSettingsAdminTab = () => {
    const [cfg, setCfg] = useState(null);
    const [enabled, setEnabled] = useState(true);
    const [levelsStr, setLevelsStr] = useState('');
    const [maxWin, setMaxWin] = useState('');
    const [report, setReport] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const apply = (c) => {
        setCfg(c);
        setEnabled(!!c.enabled);
        setLevelsStr((c.bet_levels || []).join(', '));
        setMaxWin(String(c.max_win_per_round));
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/panda-paradise/settings`, { withCredentials: true });
            apply(data);
            const { data: rep } = await axios.get(`${API}/admin/panda-paradise/report?days=30`, { withCredentials: true });
            setReport(rep);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Panda Paradise settings');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const bet_levels = levelsStr.split(',').map((s) => parseFloat(s.trim())).filter((n) => !isNaN(n) && n > 0);
        if (!bet_levels.length) { toast.error('Enter at least one positive bet level'); return; }
        const mw = parseFloat(maxWin);
        if (isNaN(mw) || mw <= 0) { toast.error('Max win must be greater than 0'); return; }
        setSaving(true);
        try {
            const { data } = await axios.put(`${API}/admin/panda-paradise/settings`,
                { enabled, bet_levels, max_win_per_round: mw }, { withCredentials: true });
            apply(data);
            toast.success('Panda Paradise settings saved');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    if (!cfg) return <div className="text-[#A0AAB2] py-12 text-center">Loading Panda Paradise settings…</div>;

    return (
        <div className="card-dark p-5 space-y-4" data-testid="pp-settings-admin">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-[#2fd07a]" />
                    <h3 className="text-sm uppercase font-bold text-white tracking-wide">Panda Paradise — Settings</h3>
                </div>
                <Button size="sm" variant="ghost" onClick={load} className="text-[#A0AAB2] h-8 text-xs" data-testid="pp-settings-refresh">
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            {report && (
                <div className="grid grid-cols-3 gap-2 text-center">
                    {[['Handle (30d)', `$${report.handle}`], ['GGR / Hold', `$${report.ggr} · ${report.hold_pct}%`],
                      ['Actual RTP', `${report.actual_rtp_pct}%`], ['Spins', report.spins],
                      ['Players', report.unique_players], ['Wins', `$${report.wins}`]].map(([l, v]) => (
                        <div key={l} className="p-2 rounded-lg border border-white/10 bg-white/[0.02]">
                            <div className="text-[9px] uppercase text-[#A0AAB2] tracking-wide">{l}</div>
                            <div className="text-sm font-bold text-white">{v}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-2">
                    <Power className={`w-4 h-4 ${enabled ? 'text-[#3ad07a]' : 'text-[#ff5a5a]'}`} />
                    <div>
                        <Label className="text-white text-sm">Game enabled</Label>
                        <p className="text-[10px] text-[#A0AAB2]">When off, new sessions and spins are blocked for all players.</p>
                    </div>
                </div>
                <Switch checked={enabled} onCheckedChange={setEnabled}
                    className="data-[state=checked]:bg-[#3ad07a] data-[state=unchecked]:bg-white/20"
                    data-testid="pp-settings-enabled" />
            </div>

            <div>
                <Label className="text-white text-xs">Bet levels (dollars, comma-separated)</Label>
                <Input value={levelsStr} onChange={(e) => setLevelsStr(e.target.value)}
                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                    placeholder="0.25, 0.5, 1, 2, 5, 10, 20" data-testid="pp-settings-betlevels" />
                <p className="text-[9px] text-[#A0AAB2] mt-1">Exact TOTAL BET buttons players can choose (25 lines). Validated on every spin.</p>
            </div>

            <div>
                <Label className="text-white text-xs">Max win per round (dollars)</Label>
                <Input type="number" step="100" min="1" value={maxWin} onChange={(e) => setMaxWin(e.target.value)}
                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono max-w-[220px]"
                    data-testid="pp-settings-maxwin" />
                <p className="text-[9px] text-[#A0AAB2] mt-1">Hard cap on a single round's REGULAR win (base + Free Play). Shared MINI/MINOR/MAJOR/MEGA jackpot prizes are exempt and can exceed this.</p>
            </div>

            <Button onClick={save} disabled={saving || loading}
                className="bg-[#2fd07a] text-black hover:bg-[#2fd07a]/90 font-bold"
                data-testid="pp-settings-save">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Settings'}
            </Button>
        </div>
    );
};

export default PandaParadiseSettingsAdminTab;
